import { focusContext } from "./model";
import { resolveDagCategories } from "./layout";
import { PointQuadtree, type IndexedPoint, type SpatialBounds } from "./spatialIndex";
import type {
  DagCanvasPreferences,
  DagCategoryImportance,
  DagFrameBenchmark,
  DagHoverTarget,
  DagImpactDisplayRole,
  DagLayoutMeta,
  DagPathState,
  DagViewerEdge,
  DagViewerGraph,
  DagViewerNode,
} from "./types";
import { visibleModelClassification } from "./ModelClassificationBadge";

interface DagCanvasRendererOptions {
  onHover: (target: DagHoverTarget | null) => void;
  onSelect: (nodeIndex: number | null) => void;
  onActivate: (nodeIndex: number) => void;
  onFirstPaint: (durationMs: number) => void;
}

interface Camera {
  x: number;
  y: number;
  scale: number;
}

interface Palette {
  active: string;
  invalidated: string;
  human: string;
  operation: string;
  artifact: string;
  context: string;
  ink: string;
  muted: string;
  line: string;
  canvas: string;
  card: string;
  compareA: string;
  compareB: string;
  compareShared: string;
  impactPropagation: string;
  impactExplicit: string;
  impactStopped: string;
  impactIndeterminate: string;
  aggregate: string;
  modelGeneric: string;
  modelInstance: string;
  modelUnclassified: string;
  sansFont: string;
  monoFont: string;
}

function impactDisplayRole(node: DagViewerNode): DagImpactDisplayRole | null {
  const role = node.metadata.dag_view_role;
  return role === "impact-propagation-enabled"
    || role === "impact-explicitly-invalidated"
    || role === "impact-stopped"
    || role === "impact-indeterminate"
    || role === "impact-reachable"
    ? role
    : null;
}

interface PointerState {
  id: number;
  startX: number;
  startY: number;
  cameraX: number;
  cameraY: number;
  moved: boolean;
}

interface OverviewLayer {
  canvas: HTMLCanvasElement;
  bounds: DagLayoutMeta["bounds"];
}

const FOCUS_DURATION_MS = 240;
const MIN_SCALE = 0.02;
const MAX_SCALE = 5;
const TAU = Math.PI * 2;

/**
 * FPS measures delivered rAF cadence, while p95FrameMs measures synchronous
 * renderer work. Keeping those clocks separate prevents headless/browser
 * scheduler jitter from being misreported as time spent drawing the graph.
 */
export function summarizePanZoomFrames(
  frameIntervals: readonly number[],
  drawDurations: readonly number[],
): DagFrameBenchmark {
  const sortedWork = drawDurations.slice().sort((a, b) => a - b);
  const p95 = sortedWork[Math.min(sortedWork.length - 1, Math.floor(sortedWork.length * 0.95))] ?? 0;
  const elapsedSeconds = Math.max(0.001, frameIntervals.reduce((sum, value) => sum + value, 0) / 1_000);
  return {
    fps: drawDurations.length / elapsedSeconds,
    p95FrameMs: p95,
    frameCount: drawDurations.length,
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function smoothstep(min: number, max: number, value: number): number {
  const t = clamp((value - min) / (max - min), 0, 1);
  return t * t * (3 - 2 * t);
}

function easeOut(value: number): number {
  return 1 - (1 - value) ** 3;
}

function readColor(element: Element, property: string, fallback: string): string {
  return getComputedStyle(element).getPropertyValue(property).trim() || fallback;
}

function nodeRadius(node: DagViewerNode): number {
  const memberCount = Number(node.metadata.member_count ?? 0);
  if (node.metadata.virtual === true && memberCount > 0) return Math.min(11, 4.5 + Math.log2(memberCount + 1));
  if (node.anchor) return 4.5;
  if (node.pathState === "invalidated") return 4;
  if (node.semanticRole === "journal") return 3.1;
  if (node.semanticRole === "decision_evidence") return 2.9;
  if (node.semanticRole === "decision_status") return 2.5;
  if (node.semanticRole === "actor") return 1.7;
  if (node.type === "operation") return 2.8;
  if (node.type === "lifecycle" || node.type === "decision") return 3.4;
  return 2.6;
}

function modelClassificationColor(node: DagViewerNode, palette: Palette): string | null {
  const classification = visibleModelClassification(node)?.classification;
  if (classification === "generic") return palette.modelGeneric;
  if (classification === "instance") return palette.modelInstance;
  if (classification === "unclassified") return palette.modelUnclassified;
  return null;
}

function nodeColor(node: DagViewerNode, palette: Palette): string {
  const analyticalRole = node.metadata.dag_view_role;
  if (analyticalRole === "anchor-a" || analyticalRole === "a") return palette.compareA;
  if (analyticalRole === "anchor-b" || analyticalRole === "b") return palette.compareB;
  if (analyticalRole === "shared") return palette.compareShared;
  if (analyticalRole === "impact-explicitly-invalidated") return palette.impactExplicit;
  if (analyticalRole === "impact-propagation-enabled") return palette.impactPropagation;
  if (analyticalRole === "impact-stopped") return palette.impactStopped;
  if (analyticalRole === "impact-indeterminate") return palette.impactIndeterminate;
  if (analyticalRole === "impact-reachable") return palette.context;
  if (node.metadata.virtual === true) return palette.aggregate;
  if (node.pathState === "invalidated") return palette.invalidated;
  const modelColor = modelClassificationColor(node, palette);
  if (modelColor) return modelColor;
  if (node.pathState === "human") return palette.human;
  if (node.type === "operation") return palette.operation;
  if (node.pathState === "active") return palette.active;
  if (node.type === "artifact" || node.type === "version") return palette.active;
  return palette.context;
}

function pathGroup(source: DagViewerNode, target: DagViewerNode): DagPathState {
  if (source.pathState === "invalidated" || target.pathState === "invalidated") return "invalidated";
  if (source.pathState === "human" || target.pathState === "human") return "human";
  if (source.pathState === "active" || target.pathState === "active") return "active";
  return "context";
}

/** Artifact model context is navigable, but never an invalidation/dependency path. */
export function isModelContextEdge(edge: Pick<DagViewerEdge, "kind">): boolean {
  return edge.kind === "instance_of";
}

export function isInteractiveDagNode(node: Pick<DagViewerNode, "metadata">): boolean {
  return node.metadata.interactive !== false;
}

function shortDigest(value: string | null): string {
  if (!value) return "no digest";
  return value.length > 14 ? `${value.slice(0, 12)}…` : value;
}

function ellipsis(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

export class DagCanvasRenderer {
  private readonly context: CanvasRenderingContext2D;
  private readonly resizeObserver: ResizeObserver;
  private graph: DagViewerGraph | null = null;
  private layoutMeta: DagLayoutMeta | null = null;
  private positions = new Float32Array();
  private spatialIndex: PointQuadtree | null = null;
  private overviewLayers: { all?: OverviewLayer; story?: OverviewLayer } = {};
  private placedCount = 0;
  private receivedCount = 0;
  private camera: Camera = { x: 0, y: 0, scale: 1 };
  private width = 0;
  private height = 0;
  private dpr = 1;
  private animationFrame = 0;
  private pointer: PointerState | null = null;
  private hoveredIndex: number | null = null;
  private selectedIndex: number | null = null;
  private focusMask: Uint8Array | null = null;
  private previousFocusMask: Uint8Array | null = null;
  private filterMask: Uint8Array | null = null;
  private filteredIndices: number[] | null = null;
  private searchMask: Uint8Array | null = null;
  private searchIndices: number[] = [];
  private activeSearchIndex: number | null = null;
  private pendingCenterIndex: number | null = null;
  private searchOverviewLayer: OverviewLayer | null = null;
  private focusStartedAt = 0;
  private storyMode = false;
  private fitted = false;
  private firstPaintReported = false;
  private dataArrivedAt = 0;
  private benchmarkCancel: (() => void) | null = null;
  private compareGraph = false;
  private palette: Palette;
  private categoryKeyByNode: string[] = [];
  private preferences: DagCanvasPreferences = {
    showCategoryLabels: true,
    fontScale: 1,
    importanceByCategory: {},
  };

  constructor(private readonly canvas: HTMLCanvasElement, private readonly options: DagCanvasRendererOptions) {
    const context = canvas.getContext("2d", { alpha: false });
    if (!context) throw new Error("Canvas2D is not available");
    this.context = context;
    this.palette = this.readPalette();
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvas.parentElement ?? canvas);
    canvas.addEventListener("pointerdown", this.onPointerDown);
    canvas.addEventListener("pointermove", this.onPointerMove);
    canvas.addEventListener("pointerup", this.onPointerUp);
    canvas.addEventListener("pointercancel", this.onPointerCancel);
    canvas.addEventListener("pointerleave", this.onPointerLeave);
    canvas.addEventListener("dblclick", this.onDoubleClick);
    canvas.addEventListener("wheel", this.onWheel, { passive: false });
    canvas.addEventListener("keydown", this.onKeyDown);
    this.resize();
  }

  setGraph(graph: DagViewerGraph, dataArrivedAt = performance.now()): void {
    const sameGraph = this.graph === graph;
    this.graph = graph;
    this.compareGraph = graph.nodes.some((node) => {
      const role = node.metadata.dag_view_role;
      return role === "a" || role === "b" || role === "shared" || role === "anchor-a" || role === "anchor-b";
    });
    this.categoryKeyByNode = resolveDagCategories(graph.nodes).categoryKeyByNode;
    this.positions = new Float32Array(graph.nodes.length * 2);
    this.positions.fill(Number.NaN);
    this.layoutMeta = null;
    this.spatialIndex = null;
    this.overviewLayers = {};
    this.placedCount = 0;
    this.receivedCount = 0;
    this.fitted = false;
    this.firstPaintReported = false;
    this.dataArrivedAt = dataArrivedAt;
    this.selectedIndex = null;
    this.focusMask = null;
    this.previousFocusMask = null;
    if (!sameGraph) {
      this.filterMask = null;
      this.filteredIndices = null;
      this.searchMask = null;
      this.searchIndices = [];
      this.activeSearchIndex = null;
    }
    this.pendingCenterIndex = null;
    this.searchOverviewLayer = null;
    this.scheduleDraw();
  }

  setLayoutMeta(meta: DagLayoutMeta): void {
    this.layoutMeta = meta;
    this.spatialIndex = new PointQuadtree({
      x: meta.bounds.minX,
      y: meta.bounds.minY,
      width: meta.bounds.maxX - meta.bounds.minX,
      height: meta.bounds.maxY - meta.bounds.minY,
    });
    this.overviewLayers = {};
    this.placedCount = 0;
    this.receivedCount = 0;
    if (!this.fitted) this.fitToView();
    this.scheduleDraw();
  }

  applyLayoutChunk(start: number, chunk: Float32Array): void {
    if (!this.graph || !this.spatialIndex) return;
    this.positions.set(chunk, start * 2);
    const nodeCount = chunk.length / 2;
    this.receivedCount += nodeCount;
    for (let offset = 0; offset < nodeCount; offset += 1) {
      const index = start + offset;
      const x = chunk[offset * 2]!;
      const y = chunk[offset * 2 + 1]!;
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      this.spatialIndex.insert({ x, y, index });
      this.placedCount += 1;
    }
    if (this.pendingCenterIndex != null && this.pendingCenterIndex >= start && this.pendingCenterIndex < start + nodeCount) {
      const pending = this.pendingCenterIndex;
      this.pendingCenterIndex = null;
      this.centerOnIndex(pending);
    }
    if (this.receivedCount === this.graph.nodes.length) {
      this.buildOverviewLayer(false);
      if (this.storyMode) this.buildOverviewLayer(true);
      this.buildSearchOverviewLayer();
    }
    this.scheduleDraw();
  }

  setViewPreferences(preferences: DagCanvasPreferences): void {
    this.preferences = {
      ...preferences,
      fontScale: clamp(preferences.fontScale, 0.8, 1.6),
      importanceByCategory: { ...preferences.importanceByCategory },
    };
    this.overviewLayers = {};
    this.searchOverviewLayer = null;
    if (this.graph && this.receivedCount === this.graph.nodes.length) {
      this.buildOverviewLayer(false);
      if (this.storyMode) this.buildOverviewLayer(true);
      this.buildSearchOverviewLayer();
    }
    this.scheduleDraw();
  }

  /** Hard visibility mask. Coordinates and the spatial index stay stable. */
  setFilterMask(mask: Uint8Array | null): void {
    this.filterMask = mask && mask.length === this.graph?.nodes.length ? mask : null;
    this.filteredIndices = this.filterMask
      ? Array.from(this.filterMask, (visible, index) => visible ? index : -1).filter((index) => index >= 0)
      : null;
    if (this.hoveredIndex != null && !this.nodeIsVisible(this.hoveredIndex)) {
      this.hoveredIndex = null;
      this.options.onHover(null);
    }
    this.overviewLayers = {};
    this.searchOverviewLayer = null;
    if (this.graph && this.receivedCount === this.graph.nodes.length) {
      this.buildOverviewLayer(false);
      if (this.storyMode) this.buildOverviewLayer(true);
      this.buildSearchOverviewLayer();
    }
    this.scheduleDraw();
  }

  /** Search is an emphasis mask: non-matches stay visible to preserve context. */
  setSearchMask(mask: Uint8Array | null, activeIndex: number | null = null): void {
    this.searchMask = mask && mask.length === this.graph?.nodes.length ? mask : null;
    this.searchIndices = this.searchMask
      ? Array.from(this.searchMask, (match, index) => match ? index : -1).filter((index) => index >= 0)
      : [];
    this.activeSearchIndex = activeIndex != null && this.searchMask?.[activeIndex] ? activeIndex : null;
    this.searchOverviewLayer = null;
    if (this.graph && this.receivedCount === this.graph.nodes.length) this.buildSearchOverviewLayer();
    this.scheduleDraw();
  }

  setSelectedId(id: string | null): void {
    const candidate = id && this.graph ? this.graph.nodeIndexById.get(id) ?? null : null;
    const index = candidate != null && this.graph && isInteractiveDagNode(this.graph.nodes[candidate]!)
      ? candidate
      : null;
    this.setSelectedIndex(index);
  }

  focusNodeById(id: string): boolean {
    const index = this.graph?.nodeIndexById.get(id);
    if (index == null || !this.graph || !isInteractiveDagNode(this.graph.nodes[index]!)) return false;
    const x = this.positions[index * 2];
    const y = this.positions[index * 2 + 1];
    if (!Number.isFinite(x) || !Number.isFinite(y)) this.pendingCenterIndex = index;
    else this.centerOnIndex(index);
    return true;
  }

  fitToMask(mask: Uint8Array): void {
    if (!this.graph || !this.width || !this.height) return;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (let index = 0; index < Math.min(mask.length, this.graph.nodes.length); index += 1) {
      if (!mask[index] || !this.nodeIsVisible(index)) continue;
      const x = this.positions[index * 2];
      const y = this.positions[index * 2 + 1];
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      minX = Math.min(minX, x!);
      minY = Math.min(minY, y!);
      maxX = Math.max(maxX, x!);
      maxY = Math.max(maxY, y!);
    }
    if (!Number.isFinite(minX)) return;
    const padding = 80;
    this.camera.x = (minX + maxX) / 2;
    this.camera.y = (minY + maxY) / 2;
    this.camera.scale = clamp(Math.min(
      (this.width - padding * 2) / Math.max(80, maxX - minX),
      (this.height - padding * 2) / Math.max(80, maxY - minY),
    ), MIN_SCALE, 2.2);
    this.scheduleDraw();
  }

  setStoryMode(enabled: boolean): void {
    if (this.storyMode === enabled) return;
    this.storyMode = enabled;
    if (enabled && !this.overviewLayers.story && this.receivedCount === this.graph?.nodes.length) {
      this.buildOverviewLayer(true);
    }
    this.focusStartedAt = performance.now();
    this.scheduleDraw();
  }

  fitToView(): void {
    const bounds = this.layoutMeta?.bounds;
    if (!bounds || !this.width || !this.height) return;
    const panelAllowance = 36;
    const contentWidth = Math.max(1, bounds.maxX - bounds.minX);
    const contentHeight = Math.max(1, bounds.maxY - bounds.minY);
    this.camera.scale = clamp(Math.min(
      (this.width - panelAllowance * 2) / contentWidth,
      (this.height - panelAllowance * 2) / contentHeight,
    ), MIN_SCALE, 1.2);
    this.camera.x = (bounds.minX + bounds.maxX) / 2;
    this.camera.y = (bounds.minY + bounds.maxY) / 2;
    this.fitted = true;
    this.scheduleDraw();
  }

  zoomBy(factor: number): void {
    this.zoomAt(this.width / 2, this.height / 2, this.camera.scale * factor);
  }

  async runPanZoomBenchmark(durationMs = 1_200): Promise<DagFrameBenchmark> {
    this.benchmarkCancel?.();
    const original = { ...this.camera };
    const frameIntervals: number[] = [];
    const drawDurations: number[] = [];
    const startedAt = performance.now();
    let last = startedAt;
    let cancelled = false;
    this.benchmarkCancel = () => { cancelled = true; };

    return new Promise((resolve) => {
      const frame = (now: number) => {
        if (cancelled || now - startedAt >= durationMs) {
          this.camera = original;
          this.benchmarkCancel = null;
          this.scheduleDraw();
          resolve(summarizePanZoomFrames(frameIntervals, drawDurations));
          return;
        }
        frameIntervals.push(now - last);
        last = now;
        const progress = (now - startedAt) / durationMs;
        this.camera.x = original.x + Math.sin(progress * TAU) * (180 / original.scale);
        this.camera.y = original.y + Math.cos(progress * TAU) * (90 / original.scale);
        this.camera.scale = clamp(original.scale * (1 + Math.sin(progress * Math.PI) * 0.24), MIN_SCALE, MAX_SCALE);
        const drawStartedAt = performance.now();
        this.draw(now);
        drawDurations.push(performance.now() - drawStartedAt);
        requestAnimationFrame(frame);
      };
      requestAnimationFrame(frame);
    });
  }

  refreshTheme(): void {
    this.palette = this.readPalette();
    this.overviewLayers = {};
    if (this.graph && this.receivedCount === this.graph.nodes.length) {
      this.buildOverviewLayer(false);
      if (this.storyMode) this.buildOverviewLayer(true);
      this.buildSearchOverviewLayer();
    }
    this.scheduleDraw();
  }

  destroy(): void {
    this.resizeObserver.disconnect();
    cancelAnimationFrame(this.animationFrame);
    this.benchmarkCancel?.();
    this.canvas.removeEventListener("pointerdown", this.onPointerDown);
    this.canvas.removeEventListener("pointermove", this.onPointerMove);
    this.canvas.removeEventListener("pointerup", this.onPointerUp);
    this.canvas.removeEventListener("pointercancel", this.onPointerCancel);
    this.canvas.removeEventListener("pointerleave", this.onPointerLeave);
    this.canvas.removeEventListener("dblclick", this.onDoubleClick);
    this.canvas.removeEventListener("wheel", this.onWheel);
    this.canvas.removeEventListener("keydown", this.onKeyDown);
  }

  private readPalette(): Palette {
    return {
      active: readColor(this.canvas, "--dag-active", "#52c78d"),
      invalidated: readColor(this.canvas, "--dag-invalidated", "#f07278"),
      human: readColor(this.canvas, "--dag-human", "#e8bd62"),
      operation: readColor(this.canvas, "--dag-operation", "#78a8d1"),
      artifact: readColor(this.canvas, "--dag-artifact", "#c99047"),
      context: readColor(this.canvas, "--dag-context", "#73807a"),
      ink: readColor(this.canvas, "--dag-ink", "#f2f4f3"),
      muted: readColor(this.canvas, "--dag-muted", "#9aa49f"),
      line: readColor(this.canvas, "--dag-line", "#38403c"),
      canvas: readColor(this.canvas, "--dag-canvas", "#0d100f"),
      card: readColor(this.canvas, "--dag-card", "#151918"),
      compareA: readColor(this.canvas, "--dag-compare-a", "#55c991"),
      compareB: readColor(this.canvas, "--dag-compare-b", "#cf83ec"),
      compareShared: readColor(this.canvas, "--dag-compare-shared", "#7eb4e1"),
      impactPropagation: readColor(this.canvas, "--dag-impact-propagation", "#b77a08"),
      impactExplicit: readColor(this.canvas, "--dag-impact-explicit", "#bd454c"),
      impactStopped: readColor(this.canvas, "--dag-impact-stopped", "#65746d"),
      impactIndeterminate: readColor(this.canvas, "--dag-impact-indeterminate", "#8061a8"),
      aggregate: readColor(this.canvas, "--dag-aggregate", "#e2a95c"),
      modelGeneric: readColor(this.canvas, "--fc-ink", "currentColor"),
      modelInstance: readColor(this.canvas, "--fc-info", "currentColor"),
      modelUnclassified: readColor(this.canvas, "--fc-ink-5", "currentColor"),
      sansFont: readColor(this.canvas, "--fc-font-sans", '"TWK Everett", Inter, ui-sans-serif, sans-serif'),
      monoFont: readColor(this.canvas, "--fc-font-mono", '"JetBrains Mono", "SFMono-Regular", monospace'),
    };
  }

  private importanceForNode(index: number): DagCategoryImportance {
    const key = this.categoryKeyByNode[index];
    return key ? this.preferences.importanceByCategory[key] ?? "standard" : "standard";
  }

  private importanceVisual(index: number): { scale: number; opacity: number } {
    const importance = this.importanceForNode(index);
    if (importance === "quiet") return { scale: 0.72, opacity: 0.38 };
    if (importance === "key") return { scale: 1.5, opacity: 1 };
    return { scale: 1, opacity: 1 };
  }

  private nodePassesFilter(index: number): boolean {
    return !this.filterMask || Boolean(this.filterMask[index]);
  }

  private nodeIsVisible(index: number): boolean {
    return this.nodePassesFilter(index) || index === this.selectedIndex;
  }

  private searchAlpha(index: number): number {
    if (!this.searchMask || index === this.selectedIndex || index === this.hoveredIndex) return 1;
    return this.searchMask[index] ? 1 : 0.12;
  }

  private resize(): void {
    const rect = this.canvas.parentElement?.getBoundingClientRect() ?? this.canvas.getBoundingClientRect();
    const width = Math.max(1, Math.round(rect.width));
    const height = Math.max(1, Math.round(rect.height));
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    if (width === this.width && height === this.height && dpr === this.dpr) return;
    this.width = width;
    this.height = height;
    this.dpr = dpr;
    this.canvas.width = Math.round(width * dpr);
    this.canvas.height = Math.round(height * dpr);
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    if (this.layoutMeta && !this.fitted) this.fitToView();
    this.scheduleDraw();
  }

  private setSelectedIndex(index: number | null): void {
    if (this.selectedIndex === index || !this.graph) return;
    this.previousFocusMask = this.focusMask;
    this.selectedIndex = index;
    this.focusMask = index == null ? null : focusContext(this.graph, index);
    this.focusStartedAt = performance.now();
    this.scheduleDraw();
  }

  private centerOnIndex(index: number): void {
    const x = this.positions[index * 2];
    const y = this.positions[index * 2 + 1];
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    this.camera.x = x!;
    this.camera.y = y!;
    this.camera.scale = clamp(Math.max(this.camera.scale, 1.55), MIN_SCALE, MAX_SCALE);
    this.scheduleDraw();
  }

  private zoomAt(screenX: number, screenY: number, nextScale: number): void {
    const before = this.screenToWorld(screenX, screenY);
    this.camera.scale = clamp(nextScale, MIN_SCALE, MAX_SCALE);
    const after = this.screenToWorld(screenX, screenY);
    this.camera.x += before.x - after.x;
    this.camera.y += before.y - after.y;
    this.scheduleDraw();
  }

  private screenToWorld(x: number, y: number): { x: number; y: number } {
    return {
      x: (x - this.width / 2) / this.camera.scale + this.camera.x,
      y: (y - this.height / 2) / this.camera.scale + this.camera.y,
    };
  }

  private worldToScreen(x: number, y: number): { x: number; y: number } {
    return {
      x: (x - this.camera.x) * this.camera.scale + this.width / 2,
      y: (y - this.camera.y) * this.camera.scale + this.height / 2,
    };
  }

  private visibleWorldBounds(paddingPixels = 100): SpatialBounds {
    const topLeft = this.screenToWorld(-paddingPixels, -paddingPixels);
    const bottomRight = this.screenToWorld(this.width + paddingPixels, this.height + paddingPixels);
    return { x: topLeft.x, y: topLeft.y, width: bottomRight.x - topLeft.x, height: bottomRight.y - topLeft.y };
  }

  private hitTest(screenX: number, screenY: number): IndexedPoint | null {
    if (!this.spatialIndex || !this.graph) return null;
    const world = this.screenToWorld(screenX, screenY);
    const radius = clamp(12 / this.camera.scale, 7, 1_000);
    const candidates = this.spatialIndex.query({ x: world.x - radius, y: world.y - radius, width: radius * 2, height: radius * 2 });
    let nearest: IndexedPoint | null = null;
    let bestDistance = radius * radius;
    for (const candidate of candidates) {
      if (!this.nodeIsVisible(candidate.index)) continue;
      if (!isInteractiveDagNode(this.graph.nodes[candidate.index]!)) continue;
      const distance = (candidate.x - world.x) ** 2 + (candidate.y - world.y) ** 2;
      if (distance <= bestDistance) {
        nearest = candidate;
        bestDistance = distance;
      }
    }
    return nearest;
  }

  private updateHover(screenX: number, screenY: number): void {
    const hit = this.hitTest(screenX, screenY);
    const next = hit?.index ?? null;
    if (next === this.hoveredIndex) return;
    this.hoveredIndex = next;
    this.options.onHover(next == null ? null : { nodeIndex: next, canvasX: screenX, canvasY: screenY });
    this.canvas.style.cursor = next == null ? "grab" : "pointer";
    this.scheduleDraw();
  }

  private focusAlpha(index: number, now: number): number {
    if (!this.focusMask && !this.previousFocusMask) return 1;
    const progress = easeOut(clamp((now - this.focusStartedAt) / FOCUS_DURATION_MS, 0, 1));
    const previous = this.previousFocusMask ? (this.previousFocusMask[index] ? 1 : 0.1) : 1;
    const target = this.focusMask ? (this.focusMask[index] ? 1 : 0.1) : 1;
    return previous + (target - previous) * progress;
  }

  private storyAlpha(node: DagViewerNode): number {
    if (!this.storyMode) return 1;
    return node.pathState === "context" ? 0.12 : 1;
  }

  private scheduleDraw(): void {
    if (this.animationFrame) return;
    this.animationFrame = requestAnimationFrame((now) => {
      this.animationFrame = 0;
      this.draw(now);
    });
  }

  private draw(now: number): void {
    const ctx = this.context;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.globalAlpha = 1;
    ctx.fillStyle = this.palette.canvas;
    ctx.fillRect(0, 0, this.width, this.height);
    if (!this.graph || !this.layoutMeta || !this.spatialIndex) return;

    this.drawGuides(ctx);
    if (this.placedCount === 0) return;
    const visible = this.spatialIndex.query(this.visibleWorldBounds()).filter((point) => this.nodeIsVisible(point.index));
    if (!this.drawOverviewLayer(ctx)) {
      this.drawEdges(ctx, visible, now);
      this.drawNodes(ctx, visible, now);
    }
    this.drawMinimap(ctx);

    if (!this.firstPaintReported && visible.length > 0) {
      this.firstPaintReported = true;
      this.options.onFirstPaint(performance.now() - this.dataArrivedAt);
    }
    if (now - this.focusStartedAt < FOCUS_DURATION_MS) this.scheduleDraw();
  }

  private drawGuides(ctx: CanvasRenderingContext2D): void {
    if (!this.layoutMeta) return;
    ctx.save();
    const fontSize = 11.5 * this.preferences.fontScale;
    ctx.font = `600 ${fontSize}px ${this.palette.monoFont}`;
    ctx.textBaseline = "middle";
    for (const lane of this.layoutMeta.lanes) {
      const start = this.worldToScreen(this.layoutMeta.bounds.minX, lane.y);
      const end = this.worldToScreen(this.layoutMeta.bounds.maxX, lane.y);
      if (start.y < -20 || start.y > this.height + 20) continue;
      const importance = this.preferences.importanceByCategory[lane.key] ?? "standard";
      ctx.globalAlpha = importance === "quiet" ? 0.1 : importance === "key" ? 0.34 : 0.22;
      ctx.strokeStyle = this.palette.line;
      ctx.lineWidth = importance === "key" ? 1.5 : 1;
      ctx.beginPath();
      ctx.moveTo(0, start.y);
      ctx.lineTo(this.width, end.y);
      ctx.stroke();
      if (this.preferences.showCategoryLabels && this.camera.scale > 0.08) {
        const label = lane.label;
        const labelY = start.y - fontSize * 1.15;
        const width = ctx.measureText(label).width + 18;
        ctx.globalAlpha = importance === "quiet" ? 0.68 : 0.94;
        ctx.fillStyle = this.palette.card;
        ctx.strokeStyle = this.palette.line;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(12, labelY - fontSize * 0.86, width, fontSize * 1.72, fontSize * 0.45);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = importance === "key" ? this.palette.active : this.palette.ink;
        ctx.fillText(label, 21, labelY);
      }
    }
    for (const tick of this.layoutMeta.ticks) {
      const screen = this.worldToScreen(tick.x, this.layoutMeta.bounds.minY);
      if (screen.x < 0 || screen.x > this.width) continue;
      ctx.globalAlpha = 0.25;
      ctx.strokeStyle = this.palette.line;
      ctx.beginPath();
      ctx.moveTo(screen.x, 0);
      ctx.lineTo(screen.x, this.height);
      ctx.stroke();
      ctx.globalAlpha = 0.55;
      ctx.fillStyle = this.palette.muted;
      ctx.fillText(tick.label, screen.x + 5, this.height - 14 - (fontSize - 10) / 2);
    }
    ctx.restore();
  }

  private edgeSetForVisible(visible: IndexedPoint[]): number[] {
    if (!this.graph) return [];
    if (visible.length > this.graph.nodes.length * 0.62) return this.graph.edges.map((_, index) => index);
    const seen = new Uint8Array(this.graph.edges.length);
    const result: number[] = [];
    for (const point of visible) {
      for (const edgeIndex of this.graph.incidentEdges[point.index] ?? []) {
        if (seen[edgeIndex]) continue;
        seen[edgeIndex] = 1;
        result.push(edgeIndex);
      }
    }
    return result;
  }

  private buildOverviewLayer(story: boolean): void {
    if (!this.graph || !this.layoutMeta || this.receivedCount !== this.graph.nodes.length) return;
    const bounds = this.layoutMeta.bounds;
    const worldWidth = Math.max(1, bounds.maxX - bounds.minX);
    const worldHeight = Math.max(1, bounds.maxY - bounds.minY);
    const textureScale = Math.min(1, 4_096 / worldWidth, 2_048 / worldHeight);
    const layerCanvas = document.createElement("canvas");
    layerCanvas.width = Math.max(1, Math.ceil(worldWidth * textureScale));
    layerCanvas.height = Math.max(1, Math.ceil(worldHeight * textureScale));
    const layerContext = layerCanvas.getContext("2d");
    if (!layerContext) return;
    layerContext.setTransform(
      textureScale,
      0,
      0,
      textureScale,
      -bounds.minX * textureScale,
      -bounds.minY * textureScale,
    );
    const referenceScale = Math.max(MIN_SCALE, this.camera.scale);
    const edgeGroups: Record<DagPathState, Path2D> = {
      active: new Path2D(),
      invalidated: new Path2D(),
      human: new Path2D(),
      context: new Path2D(),
    };
    const modelContextEdges = new Path2D();
    for (const edge of this.graph.edges) {
      if (!this.nodePassesFilter(edge.source) || !this.nodePassesFilter(edge.target)) continue;
      const ax = this.positions[edge.source * 2];
      const ay = this.positions[edge.source * 2 + 1];
      const bx = this.positions[edge.target * 2];
      const by = this.positions[edge.target * 2 + 1];
      if (![ax, ay, bx, by].every(Number.isFinite)) continue;
      const path = isModelContextEdge(edge)
        ? modelContextEdges
        : edgeGroups[pathGroup(this.graph.nodes[edge.source]!, this.graph.nodes[edge.target]!)];
      path.moveTo(ax!, ay!);
      const midX = (ax! + bx!) / 2;
      path.bezierCurveTo(midX, ay!, midX, by!, bx!, by!);
    }
    const edgeColors: Record<DagPathState, string> = {
      active: this.palette.active,
      invalidated: this.palette.invalidated,
      human: this.palette.human,
      context: this.palette.context,
    };
    for (const state of ["context", "active", "human", "invalidated"] as const) {
      layerContext.strokeStyle = edgeColors[state];
      layerContext.lineWidth = (state === "context" ? 0.75 : 1.15) / referenceScale;
      layerContext.globalAlpha = (state === "context" ? 0.14 : 0.5) * (story && state === "context" ? 0.08 : 1);
      layerContext.stroke(edgeGroups[state]);
    }
    layerContext.strokeStyle = this.palette.modelInstance;
    layerContext.lineWidth = 1.15 / referenceScale;
    layerContext.globalAlpha = story ? 0.18 : 0.52;
    layerContext.setLineDash([4 / referenceScale, 3 / referenceScale]);
    layerContext.stroke(modelContextEdges);
    layerContext.setLineDash([]);
    for (let index = 0; index < this.graph.nodes.length; index += 1) {
      if (!this.nodePassesFilter(index)) continue;
      const x = this.positions[index * 2];
      const y = this.positions[index * 2 + 1];
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      const node = this.graph.nodes[index]!;
      const visual = this.importanceVisual(index);
      const alpha = (story && node.pathState === "context" ? 0.12 : 1) * visual.opacity;
      const radius = nodeRadius(node) * visual.scale / referenceScale;
      const color = nodeColor(node, this.palette);
      if (node.anchor || node.pathState === "invalidated") {
        layerContext.globalAlpha = alpha * 0.18;
        layerContext.fillStyle = color;
        layerContext.beginPath();
        layerContext.arc(x!, y!, radius * 3, 0, TAU);
        layerContext.fill();
      }
      layerContext.globalAlpha = alpha;
      layerContext.fillStyle = color;
      layerContext.beginPath();
      layerContext.arc(x!, y!, radius, 0, TAU);
      layerContext.fill();
    }
    this.overviewLayers[story ? "story" : "all"] = { canvas: layerCanvas, bounds };
  }

  private buildSearchOverviewLayer(): void {
    if (!this.searchMask || !this.graph || !this.layoutMeta || this.receivedCount !== this.graph.nodes.length) return;
    const bounds = this.layoutMeta.bounds;
    const worldWidth = Math.max(1, bounds.maxX - bounds.minX);
    const worldHeight = Math.max(1, bounds.maxY - bounds.minY);
    const textureScale = Math.min(1, 4_096 / worldWidth, 2_048 / worldHeight);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.ceil(worldWidth * textureScale));
    canvas.height = Math.max(1, Math.ceil(worldHeight * textureScale));
    const context = canvas.getContext("2d");
    if (!context) return;
    context.setTransform(textureScale, 0, 0, textureScale, -bounds.minX * textureScale, -bounds.minY * textureScale);
    const referenceScale = Math.max(MIN_SCALE, this.camera.scale);
    for (const index of this.searchIndices) {
      if (!this.nodePassesFilter(index)) continue;
      const x = this.positions[index * 2];
      const y = this.positions[index * 2 + 1];
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      const node = this.graph.nodes[index]!;
      const visual = this.importanceVisual(index);
      context.globalAlpha = visual.opacity;
      context.fillStyle = nodeColor(node, this.palette);
      context.beginPath();
      context.arc(x!, y!, Math.max(2.4 / referenceScale, nodeRadius(node) * visual.scale / referenceScale), 0, TAU);
      context.fill();
    }
    this.searchOverviewLayer = { canvas, bounds };
  }

  private drawOverviewLayer(ctx: CanvasRenderingContext2D): boolean {
    if (this.focusMask || this.camera.scale >= 0.32) return false;
    const layer = this.overviewLayers[this.storyMode ? "story" : "all"];
    if (!layer) return false;
    const topLeft = this.worldToScreen(layer.bounds.minX, layer.bounds.minY);
    const bottomRight = this.worldToScreen(layer.bounds.maxX, layer.bounds.maxY);
    ctx.save();
    ctx.globalAlpha = this.searchMask ? 0.14 : 1;
    ctx.drawImage(layer.canvas, topLeft.x, topLeft.y, bottomRight.x - topLeft.x, bottomRight.y - topLeft.y);
    if (this.searchOverviewLayer) {
      ctx.globalAlpha = 1;
      ctx.drawImage(this.searchOverviewLayer.canvas, topLeft.x, topLeft.y, bottomRight.x - topLeft.x, bottomRight.y - topLeft.y);
    }
    ctx.restore();
    const emphasizedIndex = this.hoveredIndex ?? this.activeSearchIndex ?? this.selectedIndex;
    if (emphasizedIndex != null && this.graph && this.nodeIsVisible(emphasizedIndex)) {
      const x = this.positions[emphasizedIndex * 2];
      const y = this.positions[emphasizedIndex * 2 + 1];
      if (Number.isFinite(x) && Number.isFinite(y)) {
        const node = this.graph.nodes[emphasizedIndex]!;
        const screen = this.worldToScreen(x!, y!);
        this.drawStarNode(ctx, screen.x, screen.y, node, nodeColor(node, this.palette), 1, true, this.importanceVisual(emphasizedIndex).scale * 1.35);
      }
    }
    return true;
  }

  private drawEdges(ctx: CanvasRenderingContext2D, visible: IndexedPoint[], now: number): void {
    if (!this.graph) return;
    const visibleEdgeIndices = this.edgeSetForVisible(visible);
    const weightedEdgeIndices: number[] = [];
    const modelContextEdgeIndices: number[] = [];
    const groups: Record<DagPathState, number[][]> = {
      active: [[], [], [], []],
      invalidated: [[], [], [], []],
      human: [[], [], [], []],
      context: [[], [], [], []],
    };
    for (const edgeIndex of visibleEdgeIndices) {
      const edge = this.graph.edges[edgeIndex]!;
      if (!this.nodeIsVisible(edge.source) || !this.nodeIsVisible(edge.target)) continue;
      if (isModelContextEdge(edge)) {
        modelContextEdgeIndices.push(edgeIndex);
        continue;
      }
      const aggregateCount = Number(edge.raw.metadata?.relationship_count ?? edge.raw.metadata?.aggregate_count ?? edge.raw.metadata?.weight ?? 0);
      if (Number.isFinite(aggregateCount) && aggregateCount > 1) {
        weightedEdgeIndices.push(edgeIndex);
        continue;
      }
      const endpointAlpha = Math.min(
        this.focusAlpha(edge.source, now) * this.searchAlpha(edge.source) * this.importanceVisual(edge.source).opacity,
        this.focusAlpha(edge.target, now) * this.searchAlpha(edge.target) * this.importanceVisual(edge.target).opacity,
      );
      const alphaBucket = clamp(Math.round(endpointAlpha * 3), 0, 3);
      groups[pathGroup(this.graph.nodes[edge.source]!, this.graph.nodes[edge.target]!)][alphaBucket]!.push(edgeIndex);
    }
    const color: Record<DagPathState, string> = {
      active: this.palette.active,
      invalidated: this.palette.invalidated,
      human: this.palette.human,
      context: this.palette.context,
    };

    for (const state of ["context", "active", "human", "invalidated"] as const) {
      for (let alphaBucket = 0; alphaBucket < 4; alphaBucket += 1) {
        const edgeIndices = groups[state][alphaBucket]!;
        if (!edgeIndices.length) continue;
        ctx.save();
        ctx.strokeStyle = color[state];
        ctx.lineWidth = state === "context" ? 0.75 : 1.15;
        const storyOpacity = !this.storyMode ? 1 : state === "context" ? 0.08 : 1;
        ctx.globalAlpha = (state === "context" ? 0.14 : 0.5) * storyOpacity * Math.max(0.08, alphaBucket / 3);
        ctx.beginPath();
        for (const edgeIndex of edgeIndices) {
          const edge = this.graph.edges[edgeIndex]!;
          const ax = this.positions[edge.source * 2];
          const ay = this.positions[edge.source * 2 + 1];
          const bx = this.positions[edge.target * 2];
          const by = this.positions[edge.target * 2 + 1];
          if (![ax, ay, bx, by].every(Number.isFinite)) continue;
          const a = this.worldToScreen(ax!, ay!);
          const b = this.worldToScreen(bx!, by!);
          ctx.moveTo(a.x, a.y);
          const midX = (a.x + b.x) / 2;
          ctx.bezierCurveTo(midX, a.y, midX, b.y, b.x, b.y);
        }
        ctx.stroke();
        ctx.restore();
      }
    }

    for (const edgeIndex of weightedEdgeIndices) {
      const edge = this.graph.edges[edgeIndex]!;
      const ax = this.positions[edge.source * 2];
      const ay = this.positions[edge.source * 2 + 1];
      const bx = this.positions[edge.target * 2];
      const by = this.positions[edge.target * 2 + 1];
      if (![ax, ay, bx, by].every(Number.isFinite)) continue;
      const a = this.worldToScreen(ax!, ay!);
      const b = this.worldToScreen(bx!, by!);
      const aggregateCount = Number(edge.raw.metadata?.relationship_count ?? edge.raw.metadata?.aggregate_count ?? edge.raw.metadata?.weight ?? 1);
      ctx.save();
      ctx.strokeStyle = this.palette.aggregate;
      ctx.globalAlpha = 0.42;
      ctx.lineWidth = clamp(1 + Math.log2(Math.max(1, aggregateCount)) * 0.8, 1.4, 8);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      const midX = (a.x + b.x) / 2;
      ctx.bezierCurveTo(midX, a.y, midX, b.y, b.x, b.y);
      ctx.stroke();
      ctx.restore();
    }

    for (const edgeIndex of modelContextEdgeIndices) {
      const edge = this.graph.edges[edgeIndex]!;
      const ax = this.positions[edge.source * 2];
      const ay = this.positions[edge.source * 2 + 1];
      const bx = this.positions[edge.target * 2];
      const by = this.positions[edge.target * 2 + 1];
      if (![ax, ay, bx, by].every(Number.isFinite)) continue;
      const a = this.worldToScreen(ax!, ay!);
      const b = this.worldToScreen(bx!, by!);
      ctx.save();
      ctx.strokeStyle = this.palette.modelInstance;
      ctx.globalAlpha = 0.62 * Math.max(0.12, Math.min(
        this.focusAlpha(edge.source, now) * this.searchAlpha(edge.source),
        this.focusAlpha(edge.target, now) * this.searchAlpha(edge.target),
      ));
      ctx.lineWidth = 1.15;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      const midX = (a.x + b.x) / 2;
      ctx.bezierCurveTo(midX, a.y, midX, b.y, b.x, b.y);
      ctx.stroke();
      ctx.restore();
    }

    if (this.camera.scale >= 0.82 && visibleEdgeIndices.length <= 600) {
      this.drawArrowheads(ctx, visibleEdgeIndices);
      this.drawModelContextLabels(ctx, modelContextEdgeIndices);
    }
  }

  private drawModelContextLabels(ctx: CanvasRenderingContext2D, edgeIndices: number[]): void {
    if (!this.graph) return;
    ctx.save();
    ctx.font = `600 8px ${this.palette.monoFont}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    for (const edgeIndex of edgeIndices) {
      const edge = this.graph.edges[edgeIndex]!;
      const ax = this.positions[edge.source * 2];
      const ay = this.positions[edge.source * 2 + 1];
      const bx = this.positions[edge.target * 2];
      const by = this.positions[edge.target * 2 + 1];
      if (![ax, ay, bx, by].every(Number.isFinite)) continue;
      const a = this.worldToScreen(ax!, ay!);
      const b = this.worldToScreen(bx!, by!);
      const x = (a.x + b.x) / 2;
      const y = (a.y + b.y) / 2;
      const label = "INSTANCE OF · CONTEXT ONLY";
      const width = ctx.measureText(label).width + 10;
      ctx.globalAlpha = 0.94;
      ctx.fillStyle = this.palette.card;
      ctx.fillRect(x - width / 2, y - 7, width, 14);
      ctx.fillStyle = this.palette.ink;
      ctx.fillText(label, x, y);
    }
    ctx.restore();
  }

  private drawArrowheads(ctx: CanvasRenderingContext2D, edgeIndices: number[]): void {
    if (!this.graph) return;
    ctx.save();
    for (const edgeIndex of edgeIndices) {
      const edge = this.graph.edges[edgeIndex]!;
      if (!this.nodeIsVisible(edge.source) || !this.nodeIsVisible(edge.target)) continue;
      const ax = this.positions[edge.source * 2];
      const ay = this.positions[edge.source * 2 + 1];
      const bx = this.positions[edge.target * 2];
      const by = this.positions[edge.target * 2 + 1];
      if (![ax, ay, bx, by].every(Number.isFinite)) continue;
      const a = this.worldToScreen(ax!, ay!);
      const b = this.worldToScreen(bx!, by!);
      const direction = b.x >= a.x ? 1 : -1;
      const size = 4.2;
      const inset = 9;
      const tipX = b.x - direction * inset;
      if (isModelContextEdge(edge)) {
        ctx.globalAlpha = 0.72;
        ctx.strokeStyle = this.palette.modelInstance;
        ctx.lineWidth = 1.15;
        ctx.beginPath();
        ctx.moveTo(tipX - direction * size * 1.8, b.y - size);
        ctx.lineTo(tipX, b.y);
        ctx.lineTo(tipX - direction * size * 1.8, b.y + size);
        ctx.stroke();
        continue;
      }
      ctx.globalAlpha = 0.52;
      ctx.fillStyle = nodeColor(this.graph.nodes[edge.target]!, this.palette);
      ctx.beginPath();
      ctx.moveTo(tipX, b.y);
      ctx.lineTo(tipX - direction * size * 1.8, b.y - size);
      ctx.lineTo(tipX - direction * size * 1.8, b.y + size);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
  }

  private drawNodes(ctx: CanvasRenderingContext2D, visible: IndexedPoint[], now: number): void {
    if (!this.graph) return;
    const highDetail = smoothstep(1.15, 1.85, this.camera.scale);
    const midDetail = smoothstep(0.35, 0.9, this.camera.scale);
    const cardDetail = visible.length > 450 ? 0 : highDetail;
    for (const point of visible) {
      const node = this.graph.nodes[point.index]!;
      const visual = this.importanceVisual(point.index);
      const screen = this.worldToScreen(point.x, point.y);
      const contextualAlpha = this.focusAlpha(point.index, now) * this.storyAlpha(node);
      const searchAdjustedAlpha = this.searchMask
        ? this.searchMask[point.index] ? Math.max(contextualAlpha, 0.9) : contextualAlpha * 0.12
        : contextualAlpha;
      const alpha = searchAdjustedAlpha * visual.opacity;
      if (alpha < 0.015) continue;
      const selected = point.index === this.selectedIndex;
      const hovered = point.index === this.hoveredIndex;
      const color = nodeColor(node, this.palette);
      const searchMatch = Boolean(this.searchMask?.[point.index]);
      const activeSearch = point.index === this.activeSearchIndex;
      this.drawStarNode(ctx, screen.x, screen.y, node, color, alpha, selected || hovered || searchMatch, visual.scale * (activeSearch ? 1.42 : searchMatch ? 1.12 : 1));
      if (midDetail > 0.02) this.drawShapeNode(ctx, screen.x, screen.y, node, color, alpha * midDetail, selected, hovered, visual.scale);
      const importanceCardDetail = visual.scale > 1 ? smoothstep(0.82, 1.5, this.camera.scale) : cardDetail;
      const analyticalRole = node.metadata.dag_view_role;
      const compareLabel = analyticalRole === "anchor-a" || analyticalRole === "anchor-b" || selected || hovered;
      if (this.graph.nodes.length <= 32 && importanceCardDetail < 0.18 && (!this.compareGraph || compareLabel)) {
        this.drawCompactLabel(ctx, screen.x, screen.y, node, color, alpha, selected || hovered);
      }
      if (importanceCardDetail > 0.02) this.drawCardNode(ctx, screen.x, screen.y, node, color, alpha * importanceCardDetail, selected, hovered, visual.scale);
    }
  }

  private drawStarNode(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    node: DagViewerNode,
    color: string,
    alpha: number,
    emphasized: boolean,
    importanceScale: number,
  ): void {
    const radius = nodeRadius(node) * importanceScale + (emphasized ? 1.5 : 0);
    ctx.save();
    const impactRole = impactDisplayRole(node);
    if (
      node.anchor
      || node.pathState === "invalidated"
      || impactRole === "impact-propagation-enabled"
      || impactRole === "impact-explicitly-invalidated"
      || impactRole === "impact-indeterminate"
      || emphasized
    ) {
      ctx.globalAlpha = alpha * 0.18;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, radius * 3, 0, TAU);
      ctx.fill();
    }
    ctx.globalAlpha = alpha;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, TAU);
    ctx.fill();
    ctx.restore();
  }

  private drawShapeNode(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    node: DagViewerNode,
    color: string,
    alpha: number,
    selected: boolean,
    hovered: boolean,
    importanceScale: number,
  ): void {
    const size = (selected || hovered ? 8.5 : 6.5) * Math.max(0.82, importanceScale);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = this.palette.canvas;
    const modelClassification = visibleModelClassification(node)?.classification ?? null;
    const modelColor = modelClassificationColor(node, this.palette);
    ctx.strokeStyle = modelColor ?? color;
    ctx.lineWidth = selected ? 2 : 1.2;
    if (modelClassification === "unclassified") ctx.setLineDash([2.2, 2.2]);
    ctx.beginPath();
    const role = node.semanticRole;
    if (role === "journal") {
      const fold = size * 0.48;
      ctx.moveTo(x - size, y - size * 0.82);
      ctx.lineTo(x + size - fold, y - size * 0.82);
      ctx.lineTo(x + size, y - size * 0.82 + fold);
      ctx.lineTo(x + size, y + size * 0.82);
      ctx.lineTo(x - size, y + size * 0.82);
      ctx.closePath();
    } else if (role === "decision_evidence") {
      ctx.rect(x - size * 0.82, y - size * 0.82, size * 1.64, size * 1.64);
    } else if (role === "decision_status") {
      ctx.arc(x, y, size * 0.88, 0, TAU);
    } else if (role === "decision") {
      for (let corner = 0; corner < 8; corner += 1) {
        const angle = Math.PI / 4 * corner + Math.PI / 8;
        const px = x + Math.cos(angle) * size;
        const py = y + Math.sin(angle) * size;
        if (corner === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.closePath();
    } else if (node.type === "operation") {
      ctx.rect(x - size, y - size * 0.72, size * 2, size * 1.44);
    } else if (role === "actor") {
      ctx.moveTo(x, y - size);
      ctx.lineTo(x + size, y);
      ctx.lineTo(x, y + size);
      ctx.lineTo(x - size, y);
      ctx.closePath();
    } else if (node.type === "lifecycle") {
      for (let corner = 0; corner < 6; corner += 1) {
        const angle = Math.PI / 3 * corner;
        const px = x + Math.cos(angle) * size;
        const py = y + Math.sin(angle) * size;
        if (corner === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.closePath();
    } else {
      ctx.arc(x, y, size, 0, TAU);
    }
    ctx.fill();
    ctx.stroke();
    ctx.setLineDash([]);
    if (role === "decision_status") {
      ctx.beginPath();
      ctx.fillStyle = color;
      ctx.arc(x, y, Math.max(1.2, size * 0.22), 0, TAU);
      ctx.fill();
    } else if (role === "journal") {
      const fold = size * 0.48;
      ctx.beginPath();
      ctx.moveTo(x + size - fold, y - size * 0.82);
      ctx.lineTo(x + size - fold, y - size * 0.82 + fold);
      ctx.lineTo(x + size, y - size * 0.82 + fold);
      ctx.stroke();
    } else if (role === "artifact" && modelClassification === "generic") {
      ctx.beginPath();
      ctx.strokeStyle = this.palette.modelGeneric;
      ctx.lineWidth = Math.max(1, selected ? 1.8 : 1.1);
      ctx.arc(x, y, size * 1.34, 0, TAU);
      ctx.stroke();
    } else if (role === "artifact" && modelClassification === "instance") {
      ctx.beginPath();
      ctx.strokeStyle = this.palette.modelInstance;
      ctx.lineWidth = Math.max(1, selected ? 1.8 : 1.1);
      ctx.moveTo(x + size * .64, y + size * .64);
      ctx.lineTo(x + size * 1.25, y + size * 1.25);
      ctx.stroke();
      ctx.fillStyle = this.palette.modelInstance;
      ctx.fillRect(x + size * 1.08, y + size * 1.08, size * .34, size * .34);
    }
    ctx.restore();
  }

  private drawCompactLabel(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    node: DagViewerNode,
    color: string,
    alpha: number,
    emphasized: boolean,
  ): void {
    const fontScale = this.preferences.fontScale;
    const memberCount = Number(node.metadata.member_count ?? 0);
    const suffix = node.metadata.virtual === true && memberCount > 0 ? ` · ${memberCount}` : "";
    const label = `${ellipsis(node.label, 22)}${suffix}`;
    const fontSize = 10.5 * fontScale;
    const horizontalPadding = 7 * fontScale;
    const height = 22 * fontScale;
    ctx.save();
    ctx.font = `${emphasized ? 650 : 500} ${fontSize}px ${this.palette.sansFont}`;
    const width = Math.min(174 * fontScale, ctx.measureText(label).width + horizontalPadding * 2);
    const left = x - width / 2;
    const top = node.metadata.dag_view_role === "anchor-b" ? y + 10 : y - height - 10;
    ctx.globalAlpha = Math.max(0.68, alpha * 0.92);
    ctx.fillStyle = this.palette.card;
    ctx.strokeStyle = emphasized ? color : this.palette.line;
    ctx.lineWidth = emphasized ? 1.5 : 1;
    ctx.beginPath();
    ctx.roundRect(left, top, width, height, 5 * fontScale);
    ctx.fill();
    ctx.stroke();
    ctx.globalAlpha = Math.max(0.8, alpha);
    ctx.fillStyle = emphasized ? color : this.palette.ink;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(label, x, top + height / 2, width - horizontalPadding * 2);
    ctx.restore();
  }

  private drawCardNode(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    node: DagViewerNode,
    color: string,
    alpha: number,
    selected: boolean,
    hovered: boolean,
    importanceScale: number,
  ): void {
    const fontScale = this.preferences.fontScale * (importanceScale > 1 ? 1.04 : 1);
    const width = 194 * fontScale;
    const height = 70 * fontScale;
    const left = x - width / 2;
    const top = y - height / 2;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = this.palette.card;
    ctx.strokeStyle = selected || hovered ? color : this.palette.line;
    ctx.lineWidth = selected ? 2 : 1;
    ctx.beginPath();
    ctx.roundRect(left, top, width, height, 7 * fontScale);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = color;
    ctx.fillRect(left, top, 3.5 * fontScale, height);
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = this.palette.ink;
    ctx.font = `500 ${13 * fontScale}px ${this.palette.sansFont}`;
    ctx.fillText(ellipsis(node.label, 28), left + 13 * fontScale, top + 23 * fontScale);
    ctx.fillStyle = this.palette.muted;
    ctx.font = `${10.5 * fontScale}px ${this.palette.monoFont}`;
    const virtual = node.metadata.virtual === true;
    const memberCount = Number(node.metadata.member_count ?? 0);
    const internalCount = Number(node.metadata.internal_edge_count ?? 0);
    const incomingCount = Number(node.metadata.incoming_edge_count ?? 0);
    const outgoingCount = Number(node.metadata.outgoing_edge_count ?? 0);
    const modelClassification = visibleModelClassification(node)?.classification ?? null;
    const classificationLabel = modelClassification === "generic"
      ? "generic model"
      : modelClassification === "instance"
        ? "model instance"
        : modelClassification === "unclassified"
          ? "unclassified"
          : null;
    ctx.fillText(
      virtual
        ? `${memberCount} records · ${internalCount} internal`
        : classificationLabel
          ? `${ellipsis(node.kind, 18)} · ${classificationLabel}`
          : `${ellipsis(node.kind, 20)} · ${node.state ?? "recorded"}`,
      left + 13 * fontScale,
      top + 42 * fontScale,
    );
    ctx.fillText(
      virtual
        ? `${incomingCount} in · ${outgoingCount} out`
        : classificationLabel
          ? `${node.state ?? "recorded"} · ${shortDigest(node.digest)}`
          : shortDigest(node.digest),
      left + 13 * fontScale,
      top + 59 * fontScale,
    );
    ctx.restore();
  }

  private drawMinimap(ctx: CanvasRenderingContext2D): void {
    if (!this.graph || !this.layoutMeta || this.placedCount < 2 || this.width < 620 || this.height < 380) return;
    const width = 150;
    const height = 84;
    const left = this.width - width - 14;
    const top = this.height - height - 14;
    const bounds = this.layoutMeta.bounds;
    const worldWidth = bounds.maxX - bounds.minX;
    const worldHeight = bounds.maxY - bounds.minY;
    ctx.save();
    ctx.globalAlpha = 0.8;
    ctx.fillStyle = this.palette.card;
    ctx.strokeStyle = this.palette.line;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(left, top, width, height, 5);
    ctx.fill();
    ctx.stroke();
    const candidates = this.filteredIndices;
    const candidateCount = candidates?.length ?? this.graph.nodes.length;
    const stride = Math.max(1, Math.floor(candidateCount / 2_000));
    ctx.fillStyle = this.palette.muted;
    ctx.globalAlpha = 0.45;
    for (let cursor = 0; cursor < candidateCount; cursor += stride) {
      const index = candidates?.[cursor] ?? cursor;
      if (!this.nodeIsVisible(index)) continue;
      const x = this.positions[index * 2];
      const y = this.positions[index * 2 + 1];
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      ctx.fillRect(left + ((x! - bounds.minX) / worldWidth) * width, top + ((y! - bounds.minY) / worldHeight) * height, 1, 1);
    }
    const view = this.visibleWorldBounds(0);
    ctx.globalAlpha = 0.8;
    ctx.strokeStyle = this.palette.active;
    const viewMinX = clamp(view.x, bounds.minX, bounds.maxX);
    const viewMinY = clamp(view.y, bounds.minY, bounds.maxY);
    const viewMaxX = clamp(view.x + view.width, bounds.minX, bounds.maxX);
    const viewMaxY = clamp(view.y + view.height, bounds.minY, bounds.maxY);
    ctx.strokeRect(
      left + ((viewMinX - bounds.minX) / worldWidth) * width,
      top + ((viewMinY - bounds.minY) / worldHeight) * height,
      ((viewMaxX - viewMinX) / worldWidth) * width,
      ((viewMaxY - viewMinY) / worldHeight) * height,
    );
    ctx.restore();
  }

  private onPointerDown = (event: PointerEvent): void => {
    if (event.button !== 0) return;
    const rect = this.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    this.pointer = { id: event.pointerId, startX: x, startY: y, cameraX: this.camera.x, cameraY: this.camera.y, moved: false };
    this.canvas.setPointerCapture(event.pointerId);
    this.canvas.style.cursor = "grabbing";
  };

  private onPointerMove = (event: PointerEvent): void => {
    const rect = this.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    if (this.pointer?.id === event.pointerId) {
      const dx = x - this.pointer.startX;
      const dy = y - this.pointer.startY;
      if (Math.abs(dx) + Math.abs(dy) > 4) this.pointer.moved = true;
      if (this.pointer.moved) {
        this.camera.x = this.pointer.cameraX - dx / this.camera.scale;
        this.camera.y = this.pointer.cameraY - dy / this.camera.scale;
        this.options.onHover(null);
        this.hoveredIndex = null;
        this.scheduleDraw();
      }
      return;
    }
    this.updateHover(x, y);
  };

  private onPointerUp = (event: PointerEvent): void => {
    if (this.pointer?.id !== event.pointerId) return;
    const rect = this.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const moved = this.pointer.moved;
    this.pointer = null;
    this.canvas.releasePointerCapture(event.pointerId);
    if (!moved) {
      const hit = this.hitTest(x, y);
      this.setSelectedIndex(hit?.index ?? null);
      this.options.onSelect(hit?.index ?? null);
    }
    this.canvas.style.cursor = this.hoveredIndex == null ? "grab" : "pointer";
  };

  private onPointerCancel = (): void => {
    this.pointer = null;
    this.canvas.style.cursor = "grab";
  };

  private onPointerLeave = (): void => {
    if (this.pointer) return;
    this.hoveredIndex = null;
    this.options.onHover(null);
    this.scheduleDraw();
  };

  private onDoubleClick = (event: MouseEvent): void => {
    const rect = this.canvas.getBoundingClientRect();
    const hit = this.hitTest(event.clientX - rect.left, event.clientY - rect.top);
    if (hit) {
      this.centerOnIndex(hit.index);
      this.options.onActivate(hit.index);
    }
  };

  private onWheel = (event: WheelEvent): void => {
    event.preventDefault();
    const rect = this.canvas.getBoundingClientRect();
    const factor = Math.exp(-event.deltaY * 0.0012);
    this.zoomAt(event.clientX - rect.left, event.clientY - rect.top, this.camera.scale * factor);
  };

  private onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === "+" || event.key === "=") this.zoomBy(1.25);
    else if (event.key === "-") this.zoomBy(0.8);
    else if (event.key === "0") this.fitToView();
    else if (event.key === "Escape") {
      this.setSelectedIndex(null);
      this.options.onSelect(null);
    } else return;
    event.preventDefault();
  };
}
